import type { CevroMessengerConfig } from '../types';
import { createElement } from '../utils/dom';

/**
 * Launcher component - the chat bubble that opens/closes the chat window
 */
export class Launcher {
  private config: CevroMessengerConfig;
  private element: HTMLElement | null = null;
  private unreadBadge: HTMLElement | null = null;
  private onClick: () => void;
  private bubbleIconUrl?: string;

  constructor(config: CevroMessengerConfig, onClick: () => void, bubbleIconUrl?: string) {
    this.config = config;
    this.onClick = onClick;
    this.bubbleIconUrl = bubbleIconUrl;
  }

  /**
   * Render the launcher
   */
  render(): HTMLElement {
    this.element = createElement('div', {
      className: 'cevro-launcher',
      'data-testid': 'launcher',
    });

    // Apply positioning from config
    this.applyPositioning();

    const button = createElement('button', {
      className: 'cevro-launcher__button',
      'aria-label': 'Open chat',
      type: 'button',
      'data-testid': 'launcher-button',
    });

    if (this.bubbleIconUrl) {
      const chatImg = createElement('img', {
        className: 'cevro-launcher__icon cevro-launcher__icon--chat cevro-launcher__icon--custom',
        src: this.bubbleIconUrl,
        alt: 'Chat',
      }) as HTMLImageElement;
      chatImg.style.width = '28px';
      chatImg.style.height = '28px';
      chatImg.style.objectFit = 'contain';
      button.appendChild(chatImg);
    } else {
      const chatIcon = this.createChatIcon();
      chatIcon.classList.add('cevro-launcher__icon', 'cevro-launcher__icon--chat');
      button.appendChild(chatIcon);
    }

    // Close icon (closed state)
    const closeIcon = this.createCloseIcon();
    closeIcon.classList.add('cevro-launcher__icon', 'cevro-launcher__icon--close');
    button.appendChild(closeIcon);

    // Unread badge
    this.unreadBadge = createElement('span', {
      className: 'cevro-launcher__badge',
      'data-testid': 'unread-badge',
    });
    this.unreadBadge.style.display = 'none';
    button.appendChild(this.unreadBadge);

    button.addEventListener('click', this.onClick);
    this.element.appendChild(button);

    return this.element;
  }

  /**
   * Apply positioning styles from config
   */
  private applyPositioning(): void {
    if (!this.element) return;

    const alignment = this.config.alignment || 'right';
    const vPadding = this.config.verticalPadding ?? 20;
    const hPadding = this.config.horizontalPadding ?? 20;

    this.element.style.bottom = `${vPadding}px`;

    if (alignment === 'left') {
      this.element.style.left = `${hPadding}px`;
      this.element.style.right = 'auto';
    } else {
      this.element.style.right = `${hPadding}px`;
      this.element.style.left = 'auto';
    }

    if (this.config.zIndex) {
      this.element.style.zIndex = String(this.config.zIndex);
    }
  }

  /**
   * Create chat icon SVG
   */
  private createChatIcon(): SVGSVGElement {
    const svg = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
    svg.setAttribute('viewBox', '0 0 24 24');
    svg.setAttribute('fill', 'none');
    svg.setAttribute('stroke', 'currentColor');
    svg.setAttribute('stroke-width', '2');
    svg.setAttribute('stroke-linecap', 'round');
    svg.setAttribute('stroke-linejoin', 'round');

    const path = document.createElementNS('http://www.w3.org/2000/svg', 'path');
    path.setAttribute(
      'd',
      'M21 11.5a8.38 8.38 0 0 1-.9 3.8 8.5 8.5 0 0 1-7.6 4.7 8.38 8.38 0 0 1-3.8-.9L3 21l1.9-5.7a8.38 8.38 0 0 1-.9-3.8 8.5 8.5 0 0 1 4.7-7.6 8.38 8.38 0 0 1 3.8-.9h.5a8.48 8.48 0 0 1 8 8v.5z'
    );

    svg.appendChild(path);
    return svg;
  }

  /**
   * Create close icon SVG
   */
  private createCloseIcon(): SVGSVGElement {
    const svg = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
    svg.setAttribute('viewBox', '0 0 24 24');
    svg.setAttribute('fill', 'none');
    svg.setAttribute('stroke', 'currentColor');
    svg.setAttribute('stroke-width', '2');
    svg.setAttribute('stroke-linecap', 'round');
    svg.setAttribute('stroke-linejoin', 'round');

    const line1 = document.createElementNS('http://www.w3.org/2000/svg', 'line');
    line1.setAttribute('x1', '18');
    line1.setAttribute('y1', '6');
    line1.setAttribute('x2', '6');
    line1.setAttribute('y2', '18');

    const line2 = document.createElementNS('http://www.w3.org/2000/svg', 'line');
    line2.setAttribute('x1', '6');
    line2.setAttribute('y1', '6');
    line2.setAttribute('x2', '18');
    line2.setAttribute('y2', '18');

    svg.appendChild(line1);
    svg.appendChild(line2);
    return svg;
  }

  /**
   * Set launcher state (open/closed)
   */
  setState(isOpen: boolean): void {
    if (this.element) {
      if (isOpen) {
        this.element.classList.add('cevro-launcher--open');
      } else {
        this.element.classList.remove('cevro-launcher--open');
      }
    }
  }

  /**
   * Update unread count
   */
  setUnreadCount(count: number): void {
    if (this.unreadBadge) {
      if (count > 0) {
        this.unreadBadge.textContent = count > 99 ? '99+' : String(count);
        this.unreadBadge.style.display = 'flex';
      } else {
        this.unreadBadge.style.display = 'none';
      }
    }
  }

  /**
   * Show/hide the launcher
   */
  setVisible(visible: boolean): void {
    if (this.element) {
      this.element.style.display = visible ? 'block' : 'none';
    }
  }

  /**
   * Add pulse animation to attract attention
   */
  pulse(): void {
    if (this.element) {
      this.element.classList.add('cevro-launcher--pulse');
      setTimeout(() => {
        this.element?.classList.remove('cevro-launcher--pulse');
      }, 1000);
    }
  }

  /**
   * Update bubble icon dynamically (for preview)
   */
  updateBubbleIcon(iconUrl?: string): void {
    this.bubbleIconUrl = iconUrl;

    if (!this.element) {
      return;
    }

    const button = this.element.querySelector('.cevro-launcher__button');
    if (!button) {
      return;
    }

    const existingIcon = button.querySelector('.cevro-launcher__icon--chat');
    if (existingIcon) {
      existingIcon.remove();
    }

    if (iconUrl) {
      const chatImg = document.createElement('img');
      chatImg.className = 'cevro-launcher__icon cevro-launcher__icon--chat cevro-launcher__icon--custom';
      chatImg.src = iconUrl;
      chatImg.alt = 'Chat';
      chatImg.style.cssText = 'width: 28px !important; height: 28px !important; object-fit: contain; border-radius: 4px;';
      button.insertBefore(chatImg, button.firstChild);
    } else {
      const chatIcon = this.createChatIcon();
      chatIcon.classList.add('cevro-launcher__icon', 'cevro-launcher__icon--chat');
      button.insertBefore(chatIcon, button.firstChild);
    }
  }

  /**
   * Destroy the launcher
   */
  destroy(): void {
    if (this.element && this.element.parentNode) {
      this.element.parentNode.removeChild(this.element);
    }
    this.element = null;
    this.unreadBadge = null;
  }
}
